/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class BestFriendsAnimalSociety extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/bf-best-friends-animal-society", market);
    const alt = "Lasting Smiles";
    const title = "Best Friends Animal Society · Partners · Lasting Smiles";
    const description = "Give back to Best Friends Animal Society with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/bf-best-friends-animal-society", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/bf-best-friends-animal-society", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/bf-best-friends-animal-society", market),
                    "name": "Best Friends Animal Society",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/bf-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/bf-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Best Friends Animal Society"
                className="block mx-auto"
                src={require("../../public/images/logos/bf-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Best Friends Animal Society
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              Best Friends Animal Society, founded in 1984, is a leading national animal welfare organization dedicated to ending the killing of dogs and cats in the United States.
              Best Friends has helped reduce the number of animals killed in shelters nationwide from an estimated 17 million per year to around 625,000.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Best Friends Animal Society Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/bf/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Best Friends Animal Society Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/bf/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Best Friends Animal Society Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/bf/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/bf/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Best Friends Animal Society Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/bf/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              Your support helps run lifesaving programs in partnership with more than 3,100 animal welfare groups across the country.
              Best Friends also has lifesaving centers in New York City, Los Angeles, Atlanta and Salt Lake City, and operates the nation's largest no-kill sanctuary for companion animals.
              That means there are still more than 1,700 dogs and cats killed every day in shelters, just because they don't have safe places to call home.
              Determined to bring the country to no-kill by the year 2025, Best Friends and its nationwide network of members and partners are working to all animals in shelters.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Hunter finds his happy place
            </h2>
            <p className="text-center">
              Hunter is such a sweet and loving boy, but the hustle and bustle of the city made him quite anxious.
              After a few attempts at finding the right home, he finally met his forever family.
              Hunter loved them from the start, and it was obvious that the feeling was mutual.
              They also happened to live out on Long Island &ndash; far away from the bustle of the city.
              For a high-strung boy like Hunter, this was perfect.
              Since going to his forever home, Hunter has flourished and has become quite the couch potato.
              He has finally found his happy place.
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Watch how Best Friends Animal Society creates lasting smiles for dogs and cats.
            </h2>
            <LazyLoad
              className="min-h-200 relative sm:min-h-400 my-5"
              offset={250}
            >
              <iframe
                allowFullScreen={true}
                className="absolute h-full left-0 top-0 w-full"
                src="https://www.youtube.com/embed/oqtNkbI-LYE"
                title="Get to know Best Friends Animal Society"
              />
            </LazyLoad>
          </div>
          <div className="text-center">
            <Link
              as={toRelativePageURL("/products", market as string)}
              href={{
                pathname: "/products",
                query: { market },
              }}
            >
              <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                Shop &amp; Make a Difference
              </a>
            </Link>
          </div>
        </section>
      </>
    );
  }
}
